import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { MarketingChromeLayout } from '@/components/layout/MarketingChromeLayout';
import { AdminGuard } from '@/components/auth/AdminGuard';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { RoleRedirect } from '@/components/auth/RoleRedirect';
import { OnboardingGuard } from '@/components/auth/OnboardingGuard';
import { Dashboard } from '@/pages/Dashboard';
import { UsersPage } from '@/pages/Users';
import { DeletionRequestsPage } from '@/pages/DeletionRequests';
import { OnboardingPage } from '@/pages/Onboarding';
import { SignInPage } from '@/pages/SignIn';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { UnauthorizedPage } from '@/pages/Unauthorized';
import { NotFoundPage } from '@/pages/NotFound';
import { Interview } from '@/pages/Interview';
import { MemoryArchive } from '@/pages/MemoryArchive';
import { PersonasPage } from '@/pages/Personas';
import { StoryCardsPage } from '@/pages/StoryCards';
import { QaMatrixPage } from '@/pages/admin/QaMatrix';
import { ProductOfferingsPage } from '@/pages/admin/ProductOfferings';
import { AddonProductsPage } from '@/pages/admin/AddonProducts';
import { BlogPostsPage } from '@/pages/admin/BlogPosts';
import { BlogPostEditorPage } from '@/pages/admin/BlogPostEditor';
import { TermsPage } from '@/pages/onboarding/TermsPage';
import { PrivacyPage } from '@/pages/onboarding/PrivacyPage';
import { DownloadAppPage } from '@/pages/onboarding/DownloadAppPage';
import { FunnelGuard } from '@/components/funnel/FunnelGuard';
import { FunnelEntryPage } from '@/pages/funnel/FunnelEntryPage';
import { FunnelAccountPage } from '@/pages/funnel/FunnelAccountPage';
import { AgreementPage } from '@/pages/funnel/AgreementPage';
import { ProductSelectionPage } from '@/pages/funnel/ProductSelectionPage';
import { ShippingPage } from '@/pages/funnel/ShippingPage';
import { PaymentPage } from '@/pages/funnel/PaymentPage';
import { ConfirmationPage } from '@/pages/funnel/ConfirmationPage';
import { AccountStubPage } from '@/pages/AccountStub';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  console.warn('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

function App() {
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY || ''}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* Auth routes. /sign-up permanently redirects into the purchase
                funnel's Create Account step — the standalone signup was the
                legacy pre-funnel flow (ended at /welcome/* onboarding with no
                agreement/product/payment) and stranded new customers. The
                SignUpPage component itself lives on, embedded in
                FunnelAccountPage (review F8). */}
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sign-up" element={<Navigate to="/start/account" replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Role-based redirect */}
            <Route
              path="/"
              element={
                <AuthGuard>
                  <RoleRedirect />
                </AuthGuard>
              }
            />

            {/* Purchase funnel (design §4.1). /start and /start/account are
                public — /start/account renders the signup form when signed
                out and the guarded intent form when signed in. The remaining
                steps are clerk-authed and step-routed by FunnelGuard. */}
            <Route path="/start" element={<FunnelEntryPage />} />
            <Route path="/start/account" element={<FunnelAccountPage />} />
            <Route
              path="/start/agreement"
              element={
                <FunnelGuard step="agreement">
                  <AgreementPage />
                </FunnelGuard>
              }
            />
            <Route
              path="/start/products"
              element={
                <FunnelGuard step="products">
                  <ProductSelectionPage />
                </FunnelGuard>
              }
            />
            <Route
              path="/start/shipping"
              element={
                <FunnelGuard step="shipping">
                  <ShippingPage />
                </FunnelGuard>
              }
            />
            <Route
              path="/start/payment"
              element={
                <FunnelGuard step="payment">
                  <PaymentPage />
                </FunnelGuard>
              }
            />
            <Route
              path="/start/confirmation"
              element={
                <FunnelGuard step="confirmation">
                  <ConfirmationPage />
                </FunnelGuard>
              }
            />

            {/* Customer account stub (review F3 — Phase 2 page grows from
                this), wrapped in the marketing site's header/footer chrome
                (ClickUp 86banyvn7). */}
            <Route
              path="/account"
              element={
                <AuthGuard>
                  <MarketingChromeLayout>
                    <AccountStubPage />
                  </MarketingChromeLayout>
                </AuthGuard>
              }
            />

            {/* User routes (onboarding) */}
            <Route
              path="/onboarding"
              element={
                <AuthGuard>
                  <OnboardingPage />
                </AuthGuard>
              }
            />

            {/* Onboarding routes (auth required, no admin role needed) */}
            <Route element={<OnboardingGuard />}>
              <Route path="/welcome/terms" element={<TermsPage />} />
              <Route path="/welcome/privacy" element={<PrivacyPage />} />
              <Route path="/welcome/download" element={<DownloadAppPage />} />
            </Route>

            {/* Protected admin routes */}
            <Route
              element={
                <AdminGuard>
                  <DashboardLayout />
                </AdminGuard>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/users" element={<UsersPage />} />
              <Route path="/dashboard/users/deletion-requests" element={<DeletionRequestsPage />} />
              <Route path="/dashboard/personas" element={<PersonasPage />} />
              <Route path="/dashboard/products" element={<ProductOfferingsPage />} />
              <Route path="/dashboard/products/addons" element={<AddonProductsPage />} />
              <Route path="/dashboard/story-cards" element={<StoryCardsPage />} />
              {/* Blog CMS — content for legacyorb.com/blog (marketing rebuilds
                  bake published posts into the static site). */}
              <Route path="/dashboard/blog" element={<BlogPostsPage />} />
              <Route path="/dashboard/blog/new" element={<BlogPostEditorPage />} />
              <Route path="/dashboard/blog/:id/edit" element={<BlogPostEditorPage />} />
              {/* AI Playground now has a single surface — Life-Story Interview.
                  Keep the old /ai-playground URL as a redirect so existing bookmarks still work. */}
              <Route
                path="/dashboard/ai-playground"
                element={<Navigate to="/dashboard/ai-playground/interview" replace />}
              />
              <Route path="/dashboard/ai-playground/interview" element={<Interview />} />
              <Route path="/dashboard/ai-playground/memory-archive" element={<MemoryArchive />} />
              {/* Brief 3 §9.1 — admin QA matrix. AdminGuard already runs;
                  the page itself adds a super_admin runtime gate so a
                  non-super_admin who bookmarks the URL gets a friendly
                  "no access" message instead of a flash of empty data. */}
              <Route path="/dashboard/qa/test-matrix" element={<QaMatrixPage />} />
            </Route>

            {/* 404 catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

export default App;
